import { useMemo } from "react"

import { createBrowserInspector } from "@statelyai/inspect"
import {
  createFileRoute,
  Outlet,
  useMatchRoute,
  useNavigate,
  redirect,
} from "@tanstack/react-router"
import { z } from "zod"

import { FlowProgressBar } from "@/components/flow-progress-bar/FlowProgressBar"
import {
  ApplicationFlowContext,
  applicationFlowMachine,
  applicationFlowSteps,
} from "@/lib/flows/application"
import {
  createMachineWithInitialState,
  initialStateFromRouteMatch,
} from "@/lib/flows/base/utils"

export const Route = createFileRoute("/_dashboard/_application-flow")({
  component: RouteComponent,
  validateSearch: z.object({
    returnTo: z.string().optional(),
  }),
  beforeLoad: ({ context: { hasPermissions } }) => {
    if (!hasPermissions(["applications.view_fundingneeds"])) {
      throw redirect({ to: "/" })
    }
  },
})

function RouteComponent() {
  const navigate = useNavigate()
  const matchRoute = useMatchRoute()
  const { returnTo } = Route.useSearch()

  const { machineLogic, machineOptions } = useMemo(() => {
    const machineLogic = createMachineWithInitialState(
      applicationFlowMachine,
      () => initialStateFromRouteMatch(applicationFlowMachine, matchRoute),
    )
    const inspect =
      import.meta.env.DEV && import.meta.env.VITE_ENABLE_XSTATE_INSPECT
        ? createBrowserInspector().inspect
        : undefined

    const overrideHandlers = returnTo
      ? {
          onNextOverride: () => navigate({ to: returnTo }),
          onBackOverride: () => navigate({ to: returnTo }),
        }
      : {}

    const machineOptions = {
      input: {
        navigate,
        ...overrideHandlers,
      },
      id: "application-flow",
      inspect,
    }

    return {
      machineLogic,
      machineOptions,
    }
  }, [matchRoute, navigate, returnTo])

  return (
    <ApplicationFlowContext.Provider
      logic={machineLogic}
      options={machineOptions}
    >
      <div className="flex flex-col gap-4">
        <FlowProgressBar
          FlowContext={ApplicationFlowContext}
          flowSteps={applicationFlowSteps}
        />
        <Outlet />
      </div>
    </ApplicationFlowContext.Provider>
  )
}
